// import { useTranslation } from "react-i18next";
import { initI18n } from "./i18n";
import { WorkflowAutomationItem } from "./interface";
import { MIMETYPE_ICON_MAP } from "./utils";
import PDFViewer from "./PDFViewer";
import { useState } from "react";
import { useTranslation } from "react-i18next";

initI18n();

interface Props {
  onClickBack: () => void;
  selectedWorkflow: string;
  automationItem?: WorkflowAutomationItem;
}

const STEPS: Record<string, Array<{ title: string, subtitle: string, image: string }>> = {
  "quotes": [
    { "title": "Accurate quotes, instantly", "subtitle": "Pull your HubSpot line items and product data straight into your quote template.", "image": "quotes-1.png" },
    { "title": "Fast, automated, delivery", "subtitle": "Automatically send quotes to customers via email and store them back in HubSpot. No manual work required.", "image": "quotes-2.png" },
    { "title": "Make it on-brand, every time", "subtitle": "Customize your quotes to look and feel like your brand, and suit your unique selling style.", "image": "quotes-3.png" },
  ],
  "proposals": [
    { "title": "Personalized with your data", "subtitle": "Instantly generate a polished client-ready deck straight from your HubSpot data, including call summaries or notes.", "image": "proposals-1.png" },
    { "title": "Sent, synced, stored", "subtitle": "Every document is automatically stored in HubSpot and backed up to your Google Drive or OneDrive.", "image": "proposals-2.png" },
    { "title": "Seamless cloud collaboration", "subtitle": "Collaborative cloud docs allow your team to edit in real time, so every proposal fits your brand, and your sales style.", "image": "proposals-3.png" },
  ],
  "contracts": [
    { "title": "Instant contracts from HubSpot", "subtitle": "Instantly generated with your HubSpot Deal data, ready to go.", "image": "contracts-1.png" },
    { "title": "Request signatures with ease", "subtitle": "Request e-signatures from your HubSpot contacts, or loop in your team for an internal review first.", "image": "contracts-2.png" },
    { "title": "Signed, synced, stored", "subtitle": "Every document is automatically stored in HubSpot and backed up to your Google Drive or OneDrive.", "image": "contracts-3.png" },
    { "title": "Now make it yours!", "subtitle": "Customize this template or upload your own branded document to tailor it to your needs.", "image": "contracts-4.png" },
  ],
}

const CTAS: Record<string, Record<string, string>> = {
  "quotes": { "text": "Customize template", "link": "https://app.portant.co/templates/tmp_ZpHlwKXVgWhCvm?auto" },
  "proposals": { "text": "Customize template", "link": "https://app.portant.co/templates/tmp_hwsgMWpWsCg2tr?auto" },
  "contracts": { "text": "Customize template", "link": "https://app.portant.co/templates/tmp_Pfh64CCfyqJxVY?auto" },
}

function AppOnboardingPreview(props: Props) {
  const { t } = useTranslation();

  const { text: buttonText, link: buttonLink } = CTAS[props.selectedWorkflow];

  const steps = STEPS[props.selectedWorkflow]!
  const [currentStep, setCurrentStep] = useState(0);

  const output = props.automationItem?.outputs[0]

  return (
    <div className="flex w-screen h-screen">
      <div className="bg-gray-100 w-full p-4" style={{ "width": "calc(100vw - 360px)" }}>
        {output &&
          <div className="flex flex-col bg-white w-full h-full rounded shadow py-2 px-4 overflow-hidden">
            <div className="flex h-16 w-full items-center py-2 flex-shrink-0">
              <div className="flex items-center gap-2 text-sm font-semibold font-gilroy flex-shrink-0">
                <img className="h-10 w-10 mr-2 rounded-md" src={MIMETYPE_ICON_MAP["application/pdf"]} alt="PDF File Icon" />
                <div className="truncate max-w-[400px]"> {output.hubspotFilePdf?.fileName}</div>
                {/* NOTE: Removed output link */}
                {/* {output.hubspotFilePdf?.fileUrl && <ExternalLinkIcon className="h-4 w-4 group-hover:text-blue" />} */}
              </div>
            </div>
            <PDFViewer pdfUrl={output.previewUrl} />
          </div>
        }
      </div>

      <div className="flex flex-col items-center py-8 px-4 bg-white w-[360px] gap-8">
        <img src='https://static.portant.co/logo-full.svg' className="h-6" alt="Portant Logo" />
        <img className="w-full aspect-square" src={`${process.env.PUBLIC_URL}/images/${steps[currentStep].image}`} alt="Informative" />
        <div className="text-4xl font-semibold font-gilroy text-center">{t(steps[currentStep].title)}</div>
        <div className="text-center font-semibold -mt-4">{t(steps[currentStep].subtitle)}</div>

        <div className="mt-auto w-full flex flex-col items-center gap-4">
          {currentStep < (steps.length - 1) &&
            <button key="cta" className="btn btn-white w-full transition-colors duration-500" onClick={() => setCurrentStep(s => (s + 1) % steps.length)}>
              {t("Next")}
            </button>
          }
          {currentStep === (steps.length - 1) &&
            <button key="cta" className="btn btn-blue w-full transition-colors duration-500" onClick={() => window.open(buttonLink, "_blank")}>
              {t(buttonText)}
            </button>
          }
          <div className="flex items-center gap-1">
            {steps.map((_, index) =>
              index === currentStep
                ? <div key={index} className="bg-blue rounded-full w-10 h-1 transition-all" />
                : <div key={index} className="bg-gray-600 rounded-full w-1 h-1 cursor-pointer transition-all" onClick={() => setCurrentStep(index)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppOnboardingPreview;
